<template>
    <DModal
        :title="__('customers.customers.vat_number_check')"
        :show="showModal"
        :is-big="true"
        :close-color-secondary="true"
        @close="closeVatIdCheckModal"
    >
        <div class="vat-id-check-gui">
            <DAlert
                v-if="vatNumberError !== null"
                class="m-0"
                color="danger"
            >
                {{ vatNumberError }}
            </DAlert>
            <DAlert
                v-if="invoiceVatNumberError !== null"
                class="m-0"
                color="danger"
            >
                {{ invoiceVatNumberError }}
            </DAlert>
            <div
                class="no-alternative-billing"
            >
                <DFormRow
                    :label="__('customers.customers.vat_number_exemption')"
                >
                    <DFormCheck
                        id="vat_number_exemption"
                        v-model="form.vat_number_exemption"
                        inline
                        :is-valid="isFormFieldValid('vat_number_exemption')"
                        :disabled="vatNumberExemptionCheckboxDisabled"
                    />
                </DFormRow>
                <div
                    v-if="!form.vat_number_exemption"
                    class="vat-id-check-form"
                >
                    <div>
                        <DFormRow
                            :label="__('customers.customers.vat_number')"
                        >
                            <DInput
                                id="check_vat_number"
                                v-model="form.vat_number"
                                :placeholder="__('customers.customers.vat_number')"
                                :is-valid="isFormFieldValid('vat_number')"
                                maxlength="45"
                            />
                        </DFormRow>
                        <DFormTextarea
                            id="vat-number-address-web-portal"
                            v-model="getVatNumberAddress"
                            :label="__('customers.customers.vat_number_messages.address_per_web_portal')"
                            rows="6"
                            class="textarea"
                            :disabled="true"
                        />
                    </div>
                    <div>
                        <div class="mb-25">
                            <DInput
                                id="check_vat_number_message"
                                :placeholder="vatNumberCheck.message"
                                :class="vatNumberCheck.class"
                                :disabled="true"
                            />
                        </div>
                        <DFormTextarea
                            id="vat-number-address-customer"
                            v-model="getVatNumberCustomerAddress"
                            :label="__('customers.customers.vat_number_messages.address_of_customer')"
                            rows="6"
                            class="textarea"
                            :disabled="true"
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="form.use_alternative_billing"
                class="has-alternative-billing"
            >
                <DFormRow
                    :label="__('customers.customers.invoice_vat_number_exemption')"
                >
                    <DFormCheck
                        id="invoice_vat_number_exemption"
                        v-model="form.invoice_vat_number_exemption"
                        inline
                        :is-valid="isFormFieldValid('invoice_vat_number_exemption')"
                        :disabled="invoiceVatNumberExemptionCheckboxDisabled"
                    />
                </DFormRow>
                <div
                    v-if="!form.invoice_vat_number_exemption"
                    class="vat-id-check-form"
                >
                    <div>
                        <DFormRow
                            :label="__('customers.customers.invoice_vat_number')"
                        >
                            <DInput
                                id="check_invoice_vat_number"
                                v-model="form.invoice_vat_number"
                                :placeholder="__('customers.customers.invoice_vat_number')"
                                :is-valid="isFormFieldValid('invoice_vat_number')"
                                maxlength="45"
                            />
                        </DFormRow>
                        <DFormTextarea
                            id="invoice-vat-number-address-web-portal"
                            v-model="getVatNumberInvoiceAddress"
                            :label="__('customers.customers.vat_number_messages.address_per_web_portal')"
                            rows="6"
                            class="textarea"
                            :disabled="true"
                        />
                    </div>
                    <div>
                        <div class="mb-25">
                            <DInput
                                id="check_invoice_vat_number_message"
                                :placeholder="invoiceVatNumberCheck.message"
                                :class="invoiceVatNumberCheck.class"
                                :disabled="true"
                            />
                        </div>
                        <DFormTextarea
                            id="invoice-vat-number-address-customer"
                            v-model="getInvoiceVatNumberCustomerAddress"
                            :label="__('customers.customers.vat_number_messages.address_of_customer')"
                            rows="6"
                            class="textarea"
                            :disabled="true"
                        />
                    </div>
                </div>
            </div>
        </div>
        <template #actions>
            <DButton
                v-if="showCheckBtn"
                :label="__('messages.check')"
                :disabled="isInLoadingState"
                @click="checkVatNumbers"
            />
            <DButton
                v-if="showSaveBtn"
                :label="__('messages.save')"
                color="primary"
                :disabled="isInLoadingState"
                @click="save"
            />
        </template>
    </DModal>
</template>

<script>

import moment from 'moment/moment'

export default {
    name: 'VatIdCheckModal',
    props: {
        customer: {
            type: Object,
            default () { return {} }
        },
        createFormData: {
            type: Object,
            default () { return {} }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        editCustomerForm: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close-modal', 'update-create-customer-vat-id'],
    data () {
        return {
            form: this.setUpFormData(),
            vatNumberWebAddress: null,
            vatNumberName: null,
            invoiceVatNumberWebAddress: null,
            invoiceVatNumberName: null,
            canSave: false,
            vatNumberError: null,
            invoiceVatNumberError: null
        }
    },
    computed: {
        showCheckBtn: function () {
            return (this.form.isDirty || this.isAnyOfVatNumbersNotChecked) && !this.isExemptionsSet()
        },
        showSaveBtn: function () {
            return !this.form.isDirty ||
                (this.canSave && this.form.vat_number_valid && (this.form.invoice_vat_number_valid || !this.form.use_alternative_billing)) ||
                this.isExemptionsSet()
        },
        vatNumberExemptionCheckboxDisabled: function () {
            return !!this.form.vat_number
        },
        invoiceVatNumberExemptionCheckboxDisabled: function () {
            return !!this.form.invoice_vat_number
        },
        vatNumberCheck: function () {
            return {
                message: this.getVatIdCheckMessage(this.form.vat_number_checked_at, this.form.vat_number_valid, this.form.isVatNumberWebPortalAvailable),
                class: this.getVatIdCheckColor(this.form.vat_number_checked_at, this.form.vat_number_valid)
            }
        },
        invoiceVatNumberCheck: function () {
            return {
                message: this.getVatIdCheckMessage(this.form.invoice_vat_number_checked_at, this.form.invoice_vat_number_valid, this.form.isInvoiceVatNumberWebPortalAvailable),
                class: this.getVatIdCheckColor(this.form.invoice_vat_number_checked_at, this.form.invoice_vat_number_valid)
            }
        },
        invoiceTitle: function () {
            if (this.form.invoice_title === null) {
                return ''
            }
            const translatedTitle = this.__(`customers.common.titles.${this.form.invoice_title.toLowerCase()}`)

            return `${translatedTitle} `
        },
        title: function () {
            if (this.form.title === null) {
                return ''
            }
            const translatedTitle = this.__(`customers.common.titles.${this.form.title.toLowerCase()}`)

            return `${translatedTitle} `
        },
        getVatNumberCustomerAddress: function () {
            let addressOfCustomer = ''
            if (this.form.firstname !== null && this.form.lastname !== null) {
                addressOfCustomer += `${this.title}${this.form.firstname} ${this.form.lastname}\n`
            }
            if (this.form.company !== null) {
                addressOfCustomer += `${this.form.company}\n`
            }
            if ((this.form.firstname !== null && this.form.lastname !== null) || this.form.company !== null) {
                addressOfCustomer += '\n'
            }
            if (this.form.address1 !== null) {
                addressOfCustomer += `${this.form.address1}\n`
            }
            if (this.form.address2 !== null) {
                addressOfCustomer += `${this.form.address2}\n`
            }
            if (this.form.postal_code !== '' || this.form.city !== '' || this.form.country_code !== '') {
                addressOfCustomer += `${this.form.country_name} ${this.form.postal_code} ${this.form.city}`
            }
            return addressOfCustomer
        },
        getInvoiceVatNumberCustomerAddress: function () {
            let invoiceAddressOfCustomer = ''
            if (this.form.invoice_firstname !== null && this.form.invoice_lastname !== null) {
                invoiceAddressOfCustomer += `${this.invoiceTitle}${this.form.invoice_firstname} ${this.form.invoice_lastname}\n`
            }
            if (this.form.invoice_company !== null) {
                invoiceAddressOfCustomer += `${this.form.invoice_company}\n`
            }
            if ((this.form.invoice_firstname !== null && this.form.invoice_lastname !== null) || this.form.invoice_company !== null) {
                invoiceAddressOfCustomer += '\n'
            }
            if (this.form.invoice_address1 !== null) {
                invoiceAddressOfCustomer += `${this.form.invoice_address1}\n`
            }
            if (this.form.invoice_address2 !== null) {
                invoiceAddressOfCustomer += `${this.form.invoice_address2}\n`
            }
            if (this.form.postal_code !== '' || this.form.city !== '' || this.form.country_code !== '') {
                invoiceAddressOfCustomer += `${this.form.country_name} ${this.form.postal_code} ${this.form.city}`
            }
            return invoiceAddressOfCustomer
        },
        getVatNumberAddress: function () {
            let vatAddress = ''
            if (this.vatNumberName !== null) {
                vatAddress += `${this.vatNumberName}\n\n`
            }
            if (this.vatNumberWebAddress !== null) {
                vatAddress += this.vatNumberWebAddress
            }
            return vatAddress
        },
        getVatNumberInvoiceAddress: function () {
            let vatInvoiceAddress = ''
            if (this.invoiceVatNumberName !== null) {
                vatInvoiceAddress += `${this.invoiceVatNumberName}\n\n`
            }
            if (this.invoiceVatNumberWebAddress !== null) {
                vatInvoiceAddress += this.invoiceVatNumberWebAddress
            }
            return vatInvoiceAddress
        }
    },
    watch: {
        'form.vat_number' () {
            this.canSave = false
        },
        'form.invoice_vat_number' () {
            this.canSave = false
        }
    },
    methods: {
        isExemptionsSet () {
            return this.form.vat_number_exemption &&
                (this.form.invoice_vat_number_exemption || !this.form.use_alternative_billing)
        },
        setUpFormData: function () {
            const data = this.editCustomerForm
                ? { ...this.createFormData }
                : (Object.keys(this.customer).length !== 0 ? { ...this.customer } : { ...this.createFormData })
            return this.$inertia.form({
                vat_number: data?.vat_number ?? '',
                vat_number_valid: data?.vat_number_valid ?? false,
                vat_number_exemption: data?.vat_number_exemption ?? false,
                vat_number_checked_at: data?.vat_number_checked_at ?? null,
                title: data?.title ?? null,
                firstname: data?.firstname ?? null,
                lastname: data?.lastname ?? null,
                company: data?.company ?? null,
                address1: data?.address1 ?? null,
                address2: data?.address2 ?? null,
                postal_code: data?.postal_code ?? '',
                city: data?.city ?? '',
                country_code: data?.country_code ?? '',
                invoice_title: data?.invoice_title ?? null,
                invoice_firstname: data?.invoice_firstname ?? null,
                invoice_lastname: data?.invoice_lastname ?? null,
                invoice_company: data?.invoice_company ?? null,
                invoice_city: data?.invoice_city ?? '',
                invoice_postal_code: data?.invoice_postal_code ?? '',
                invoice_address1: data?.invoice_address1 ?? null,
                invoice_address2: data?.invoice_address2 ?? null,
                invoice_country_code: data?.invoice_country_code ?? '',
                isVatNumberWebPortalAvailable: true,
                invoice_vat_number: data?.invoice_vat_number ?? '',
                invoice_vat_number_valid: data?.invoice_vat_number_valid ?? false,
                invoice_vat_number_exemption: data?.invoice_vat_number_exemption ?? false,
                invoice_vat_number_checked_at: data?.invoice_vat_number_checked_at ?? null,
                isInvoiceVatNumberWebPortalAvailable: true,
                use_alternative_billing: data?.use_alternative_billing ?? false,
                country_name: data?.country?.[`name_${this.$page.props.auth.user.language_code}`] ?? null
            })
        },
        closeVatIdCheckModal: function () {
            this.$emit('close-modal')
        },
        getVatIdCheckMessage: function (checkedAt, isValid, isWebPortalAvailable) {
            let message = this.__('customers.customers.vat_number_messages.not_valid', { checkedAt: moment(checkedAt).format('YYYY-MM-DD') })

            if (!isWebPortalAvailable) {
                message = this.__('customers.customers.vat_number_messages.not_available')
            } else if (checkedAt === null) {
                message = this.__('customers.customers.vat_number_messages.not_checked', { checkedAt: moment(checkedAt).format('YYYY-MM-DD') })
            } else if (isValid) {
                message = this.__('customers.customers.vat_number_messages.valid', { checkedAt: moment(checkedAt).format('YYYY-MM-DD') })
            }

            return message
        },
        getVatIdCheckColor: function (checkedAt, isValid) {
            if (checkedAt === null) {
                return 'yellow'
            }

            if (isValid) {
                return 'success'
            }

            return 'warning'
        },
        save: function () {
            if (this.editCustomerForm === false) {
                this.$inertia.post(this.$route('customers.customers.save-vat-id-data', { customer: this.customer.id }), {
                    vat_number: this.form.vat_number,
                    vat_number_exemption: this.form.vat_number_exemption,
                    vat_number_valid: this.form.vat_number_valid && !this.form.vat_number_exemption,
                    vat_number_checked_at: this.form.vat_number_checked_at,
                    invoice_vat_number: this.form.invoice_vat_number,
                    invoice_vat_number_exemption: this.form.invoice_vat_number_exemption,
                    invoice_vat_number_valid: this.form.invoice_vat_number_valid && !this.form.invoice_vat_number_exemption,
                    invoice_vat_number_checked_at: this.form.invoice_vat_number_checked_at,
                    use_alternative_billing: this.form.use_alternative_billing
                })
            } else {
                this.$emit('update-create-customer-vat-id', {
                    vat_number: this.form.vat_number,
                    vat_number_exemption: this.form.vat_number_exemption,
                    vat_number_valid: this.form.vat_number_valid,
                    vat_number_checked_at: this.form.vat_number_checked_at,
                    invoice_vat_number: this.form.invoice_vat_number,
                    invoice_vat_number_exemption: this.form.invoice_vat_number_exemption,
                    invoice_vat_number_valid: this.form.invoice_vat_number_valid,
                    invoice_vat_number_checked_at: this.form.invoice_vat_number_checked_at,
                    use_alternative_billing: this.form.use_alternative_billing
                })
            }
            this.closeVatIdCheckModal()
        },
        checkVatNumbers: function () {
            this.vatNumberError = null
            this.invoiceVatNumberError = null
            window.axios.post(this.$route('customers.customers.check-vat-id-data'), {
                vat_number: this.form.vat_number,
                invoice_vat_number: this.form.invoice_vat_number,
                use_alternative_billing: this.form.use_alternative_billing
            })
                .then((response) => {
                    const data = response.data
                    if (data.vat_number.error) {
                        this.vatNumberError = data.vat_number.error
                    } else {
                        this.form.vat_number_valid = data.vat_number.is_valid
                        this.vatNumberWebAddress = data.vat_number.address
                        this.vatNumberName = data.vat_number.name
                        this.form.isVatNumberWebPortalAvailable = data.vat_number.portal_available
                        this.form.vat_number_checked_at = data.vat_number.date
                    }

                    if (data.vat_number.error && this.form.use_alternative_billing) {
                        this.invoiceVatNumberError = data.invoice_vat_number.error
                    } else if (this.form.use_alternative_billing) {
                        this.form.invoice_vat_number_valid = data.invoice_vat_number.is_valid
                        this.invoiceVatNumberWebAddress = data.invoice_vat_number.address
                        this.invoiceVatNumberName = data.invoice_vat_number.name
                        this.form.isInvoiceVatNumberWebPortalAvailable = data.invoice_vat_number.portal_available
                        this.form.invoice_vat_number_checked_at = data.invoice_vat_number.date
                    }

                    this.canSave = this.isVatNumberValidAndHasNotAlternativeBilling() ||
                        this.webPortalIsNotAvailableAndHasNotAlternativeBilling() ||
                        this.webPortalIsNotAvailableAtAll() ||
                        this.bothVatFieldAreValidOrWebPortalIsUnavailable() ||
                        !(data.vat_number.error || data.invoice_vat_number.error)
                })
        },
        isVatNumberValidAndHasNotAlternativeBilling: function () {
            return this.form.vat_number_valid && !this.form.use_alternative_billing
        },
        webPortalIsNotAvailableAndHasNotAlternativeBilling: function () {
            return !(this.form.isVatNumberWebPortalAvailable || this.form.use_alternative_billing)
        },
        webPortalIsNotAvailableAtAll: function () {
            return !(this.form.isVatNumberWebPortalAvailable || this.form.isInvoiceVatNumberWebPortalAvailable)
        },
        bothVatFieldAreValidOrWebPortalIsUnavailable: function () {
            return this.form.use_alternative_billing &&
            (this.form.vat_number_valid || !this.form.isVatNumberWebPortalAvailable) &&
            (this.form.invoice_vat_number_valid || !this.form.isInvoiceVatNumberWebPortalAvailable)
        },
        isAnyOfVatNumbersNotChecked: function () {
            return this.form.vat_number_checked_at === null || this.form.invoice_vat_number_checked_at === null
        }
    }
}
</script>

<style lang="scss">
.vat-id-check-gui {
    .warning {
        background-color: #F8CBAD !important;
        opacity: 1;
        &::placeholder {
            text-align: center;
            color: #006100;
        }
    }
    .success {
        background-color: #C6EFCE !important;
        opacity: 1;
        &::placeholder {
            text-align: center;
            color: #006100;
        }
    }
    .yellow {
        background-color: #f5e441 !important;
        opacity: 1;
        &::placeholder {
            text-align: center;
            color: #006100;
        }
    }
    padding: 20px;
    & .has-alternative-billing {
        margin-top: 20px;
    }
    & .vat-id-check-form {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 15px;
    }
    &.textarea label {
        font-weight: bold;
    }
    & .mb-25 {
        margin-bottom: 25px;
    }
}
</style>
